import SettingsIcon from "@mui/icons-material/Settings";
import {
  Autocomplete,
  Chip,
  Grid,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Map } from "ol";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { createCustomFilterOptions } from "../functions/createCustomFilterOptions";
import {
  initialSearchSettings,
  initialTagSettings,
  readSearchSettings,
  updateSearchSettings,
} from "../functions/localSearchSettings";
import { zoomOnFeature } from "../functions/zoomOnFeature";
import {
  SearchListItem,
  SearchSettings,
  SearchTagKey,
  SearchTagSettings,
} from "../types/search";
import { ListboxComponent } from "./MapSearchVirtualization";
import SearchSettingsForm from "./SearchSettingsForm";

interface Props {
  items: SearchListItem[];
  map?: Map;
}

const useStyles = makeStyles((theme) => ({
  searchBar: {
    position: "absolute",
    top: "0.5em",
    left: theme.spacing(6),
    right: theme.spacing(12),
    maxWidth: 600,
    zIndex: theme.zIndex.appBar,
    [theme.breakpoints.down("md")]: {
      right: theme.spacing(3),
    },
  },
  searchBarFieldRoot: {
    display: "flex",
    alignItems: "center",
    maxWidth: 600,
  },
  listbox: {
    boxSizing: "border-box",
    "& ul": {
      padding: 0,
      margin: 0,
    },
  },
  tagTooltip: {
    backgroundColor: "transparent",
  },
}));

const MapSearch = ({ map, items }: Props): JSX.Element => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>();
  const { t } = useTranslation("map");
  const theme = useTheme();
  const [settings, setSettings] = useState<SearchSettings>(
    readSearchSettings("search") ?? initialSearchSettings,
  );
  const [tagSettings, setTagSettings] = useState<SearchTagSettings>(
    readSearchSettings("tags") ?? initialTagSettings,
  );

  const handleSettingsClose = () => {
    setAnchorEl(undefined);
  };
  const ref = useRef<HTMLDivElement>();

  const handleSettingsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSettings((old) => ({
      ...old,
      [event.target.name]: event.target.checked,
    }));
  };

  const handleTagSettingsChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setTagSettings((old) => ({
      ...old,
      [event.target.name]: event.target.checked,
    }));
  };

  // Update localstorage
  useEffect(() => {
    updateSearchSettings({ search: settings, tags: tagSettings });
  }, [tagSettings, settings]);

  const filteredItems = useMemo(
    () => items.filter((i) => settings[i.type]),
    [items, settings],
  );

  const filterOptions = useMemo(
    () => createCustomFilterOptions({ tagSettings }),
    [tagSettings],
  );

  return (
    <>
      <Autocomplete
        className={classes.searchBar}
        classes={{ listbox: classes.listbox }}
        options={filteredItems}
        getOptionLabel={(opt) => opt.name}
        getOptionDisabled={(opt) => !!opt.disabled}
        openText={t("open")}
        closeText={t("close")}
        clearText={t("clear")}
        noOptionsText={t("no-options")}
        disableListWrap
        ref={ref}
        clearOnBlur={false}
        ListboxComponent={
          ListboxComponent as React.ComponentType<
            React.HTMLAttributes<HTMLElement>
          >
        }
        filterOptions={filterOptions}
        renderInput={(params) => {
          const endAdornment = params.InputProps
            .endAdornment as React.ReactElement;
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
          const childs = React.Children.toArray(endAdornment.props?.children);
          childs.push(
            <IconButton
              key="settings"
              title={t("search-settings")}
              onClick={(e) => {
                e.stopPropagation();
                setAnchorEl(e.currentTarget);
              }}
              style={{ padding: 2, marginRight: -2 }}
              size="large"
            >
              <SettingsIcon />
            </IconButton>,
          );
          const endAdo = React.cloneElement(
            endAdornment,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            endAdornment.props,
            childs,
          );
          return (
            <Paper className={classes.searchBarFieldRoot}>
              <TextField
                {...params}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: endAdo,
                }}
                label={t("search")}
                variant="outlined"
              />
            </Paper>
          );
        }}
        renderOption={(props, opt) => {
          const { name, description, icon, tags, disabled, disabledText } = opt;
          const chips: JSX.Element[] = [];
          const hiddenChips: JSX.Element[] = [];
          const maxLength = (ref.current?.offsetWidth ?? 600) * 0.7;
          const characterLength = 8;
          let currLength = 0;
          if (tags) {
            const tagKeys = Object.keys(tags) as Array<SearchTagKey>;

            for (let i = 0; i < tagKeys.length; i++) {
              const k = tagKeys[i];
              const tag = tags[k];
              const val = tag?.value;
              const title = tag?.title;
              if (!val || !title) continue;

              currLength += 21 + val.length * characterLength;
              if (tag?.icon) currLength += 20;

              if (currLength < maxLength) {
                chips.push(
                  <Grid item key={k} style={{ marginRight: 5 }}>
                    <Chip
                      icon={tag?.icon}
                      label={val}
                      size="small"
                      title={`${title}: ${val}`}
                    />
                  </Grid>,
                );
              } else {
                hiddenChips.push(
                  <Grid item key={k}>
                    <Chip
                      label={`${title}: ${val}`}
                      title={title}
                      size="small"
                    />
                  </Grid>,
                );
              }
            }
          }

          return (
            <ListItem {...props}>
              {icon && <ListItemIcon>{icon}</ListItemIcon>}
              <ListItemText
                disableTypography
                primary={
                  <Typography
                    component="span"
                    title={disabled ? `${name} - ${disabledText}` : name}
                    noWrap
                    variant="body1"
                    style={{ fontWeight: theme.typography.fontWeightBold }}
                  >
                    {disabled ? `${name} - ${disabledText}` : name}
                  </Typography>
                }
                secondary={
                  <>
                    <Typography
                      color="textSecondary"
                      display="block"
                      title={description}
                      noWrap
                      variant="body2"
                    >
                      {description}
                    </Typography>
                    <Grid container>
                      {chips}
                      {hiddenChips.length > 0 && (
                        <Tooltip
                          classes={{ tooltip: classes.tagTooltip }}
                          title={
                            <Grid container spacing={1}>
                              {hiddenChips}
                            </Grid>
                          }
                          PopperProps={{ placement: "bottom-start" }}
                        >
                          <Grid item key="plusChip">
                            <Chip
                              label={`+${hiddenChips.length}`}
                              size="small"
                            />
                          </Grid>
                        </Tooltip>
                      )}
                    </Grid>
                  </>
                }
              />
            </ListItem>
          );
        }}
        isOptionEqualToValue={(opt, val) => opt.id === val.id}
        onChange={(_e, item) => {
          if (!item || !map) return;

          if (item.onSelect) {
            item.onSelect();
            return;
          }
          zoomOnFeature(map, item.id, 14);
        }}
        blurOnSelect
      />
      <SearchSettingsForm
        anchorEl={anchorEl}
        settings={settings}
        tagSettings={tagSettings}
        onClose={handleSettingsClose}
        onChange={handleSettingsChange}
        onTagsChange={handleTagSettingsChange}
      />
    </>
  );
};

export default MapSearch;
